import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { withTranslation, Trans } from 'react-i18next';
import qs from 'qs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import StarIcon from '@mui/icons-material/Star';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Form from 'components/form';
import Input from 'components/form/TextInput';
import Select from 'components/form/Select';
import { Checkbox } from 'components/form/Switch';
import Spin from 'components/Spin';
import Button from 'components/Button';
import { getItemDetails } from 'services/pages.svc';
import { createAccount } from 'services/account.service';
import { updateCart } from 'store/modules/public/actions';
import { isChecked, toDbPhone } from 'utils';
import toast from 'utils/toast';
import { APP_CONFIG, WEBSITE } from 'constants/config';
import COUNTRIES from 'constants/countries';
import { STATES } from 'constants/states';
import cart from '../cart';
import { Card } from '../styled';

import { validateMobilePhone } from '../utils/validateMobilePhone';

const FormItem = Form.FormItem;
let account_referer_id;

const normalizePhone = (v, prev) => {
  if (v === prev) return v;
  if (v.length === 15) return prev;
  return v.replace(/^(\d{3})(\d{3})(\d)+$/, '($1) $2-$3');
};

const Sidebar = styled(Box)(
  ({ theme }) => `
  background-color: ${theme.palette.primary.main};
  &:before {
    background-image: radial-gradient(120% 40%, #04abff, ${theme.palette.primary.main} 100%);
  }   
`,
);

const captchaReady = () => {
  return new Promise((resolve) => {
    grecaptcha.enterprise.ready(resolve);
  });
};

const Checkout = ({
  match: {
    params: { type },
  },
  location,
  history,
  form: { getFieldDecorator, validateFields, getFieldValue },
  t,
}) => {
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState({});
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    const query = qs.parse(location.search.substr(1));
    if (query.ref) {
      account_referer_id = query.ref;
      localStorage.setItem('ref_id', query.ref);
    } else {
      account_referer_id = localStorage.getItem('ref_id');
    }
    if (query.cart) {
      getItemDetails(query.cart).then(
        (item) => {
          setItem(item);
          setLoading(false);
        },
        (e) => {
          window.location.replace(`${WEBSITE}/pricing`);
        },
      );
    } else {
      window.location.replace(`${WEBSITE}/pricing`);
    }
  }, []);

  const onSubmit = () => {
    return validateFields()
      .then(async (values) => {
        try {
          cart.reset();
          const cardElement = elements.getElement(CardElement);
          await captchaReady();
          const recaptcha_token = await grecaptcha.enterprise.execute(
            '6LegX_IoAAAAADQht6xAwsppsFTq9zytMeeK4ffu',
            { action: 'checkout' },
          );
          const tokenResp = await stripe.createToken(cardElement);
          if (tokenResp.error) {
            throw tokenResp.error;
          }
          const resp = await createAccount({
            ...values,
            recaptcha_token,
            account_referer_id,
            account_mobile: toDbPhone(values.account_mobile),
            account_phone: toDbPhone(values.account_phone),
            cart: item.id,
            card_token: tokenResp.token.id,
            products: item.productID,
            discount: item.discountID,
            trialDuration: item.trialDuration,
            account_brand: APP_CONFIG.BRAND.toLowerCase(),
          });

          if (resp.status === 'error') {
            throw new Error(resp.message);
          } else if (!resp.account_id) {
            // trying to parse error from php error
            const matches = resp.match(/"message":"([\w\s.]+)/);
            if (matches && matches[1]) {
              throw new Error(matches[1]);
            } else {
              throw new Error(
                'There was an error. Please validate your input and try again.',
              );
            }
          } else {
            cart.set('product_id', item.id);
            cart.set('email', values.account_email);
            cart.set('account_hash', resp.hash);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push([
              'event',
              'conversion',
              { send_to: 'AW-984862540/-2FVCJanlswBEMyez9UD' },
            ]);
            dispatch(updateCart(resp));
            history.push('/offer/?i=0');
          }
        } catch (err) {
          toast.error(err.message);
        }
      })
      .catch((e) => {
        toast.error(e.message ?? 'Please verify your inputs');
      });
  };

  const matchEmail = (rules, value, cb) => {
    if (value) {
      const email = getFieldValue('account_email');
      if (value !== email) {
        cb(new Error('Please verify your email'));
      } else {
        cb();
      }
    } else {
      cb();
    }
  };

  const country = getFieldValue('account_country') || 'US';
  const noWebsite = getFieldValue('no_website');

  return (
    <>
      <Box
        sx={{
          px: {
            lg: 4,
          },
        }}
      >
        <Helmet>
          <title>Start Trial</title>
          <script
            src="https://www.google.com/recaptcha/enterprise.js?render=6LegX_IoAAAAADQht6xAwsppsFTq9zytMeeK4ffu"
            async
            defer
          ></script>
        </Helmet>
        <Spin spinning={loading}>
          <Grid container spacing={8}>
            <Grid item md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box px={4}>
                <Box className="logo">
                  <a href="/">
                    <img src={`/assets/svg/tic-logo-full.svg`} alt="logo" />
                  </a>
                </Box>
                <Typography paragraph variant="h2" fontWeight={500}>
                  Start your{' '}
                  <Typography
                    component="span"
                    color="primary"
                    fontSize="inherit"
                    fontWeight="inherit"
                  >
                    free {item.trialDuration} day trial
                  </Typography>{' '}
                  today and you'll start seeing the power of Text In Church.
                </Typography>
              </Box>
              <Card sx={{ mb: 2 }}>
                <CardContent sx={{ px: 3, py: 4 }}>
                  <Grid container spacing={4}>
                    <Grid item md={6}>
                      <Stack
                        mb={2}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <NewReleasesIcon
                          color="primary"
                          sx={{ width: 28, height: 28 }}
                        />
                        <Typography variant="h3">No Contracts</Typography>
                      </Stack>
                      <Typography color="text.secondary" lineHeight={1.6}>
                        After your{' '}
                        <Typography
                          color="primary"
                          component="span"
                          fontWeight={500}
                        >
                          FREE {item.trialDuration} day trial
                        </Typography>{' '}
                        period you'll automatically roll over to your monthly
                        membership and pay just{' '}
                        <Typography
                          color="primary"
                          component="span"
                          fontWeight={500}
                        >
                          ${item.price}/{item.freq}
                        </Typography>
                        . No Contracts. Cancel at any time.
                      </Typography>
                    </Grid>
                    <Grid item md={6}>
                      <Stack
                        mb={2}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <NewReleasesIcon
                          color="primary"
                          sx={{ width: 28, height: 28 }}
                        />
                        <Typography variant="h3">Our Guarantee</Typography>
                      </Stack>
                      <Typography color="text.secondary" lineHeight={1.6}>
                        We want to provide the best experience possible so we
                        back our product with a Full{' '}
                        <Typography
                          color="primary"
                          component="span"
                          fontWeight={500}
                        >
                          30 Day Money-Back Guarantee
                        </Typography>
                        .
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h2">{item.name}</Typography>
                    <Typography color="primary" variant="h2">
                      ${item.price} / {item.freq}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ mt: 'auto' }}>
                <CardContent>
                  <Typography gutterBottom fontWeight={500} variant="subtitle1">
                    Soo happy with Text in Church!
                  </Typography>
                  <Typography paragraph color="text.secondary">
                    {t('user-quote-text')}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{ width: 24, height: 24 }}
                        src={t('user-quote-avatar')}
                      />
                      <Typography color="text.secondary">
                        {t('user-quote-name')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <StarIcon color="secondary" />
                      <StarIcon color="secondary" />
                      <StarIcon color="secondary" />
                      <StarIcon color="secondary" />
                      <StarIcon color="secondary" />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item md={6} sx={{ flex: 1 }}>
              <Card>
                <Typography variant="h2" textAlign="center" sx={{ py: 2 }}>
                  Let’s Create Your Account
                </Typography>
                <Divider />
                <CardContent>
                  <Form>
                    <Grid container columnSpacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_name', {
                            rules: [
                              {
                                required: true,
                                message: t('account-name-required'),
                              },
                            ],
                          })(
                            <Input
                              fullWidth
                              pattern="\S.*\S"
                              placeholder={t('account-name-label')}
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_website', {
                            rules: [
                              {
                                required: !noWebsite,
                                message: t('account-website-required'),
                              },
                              {
                                type: 'url',
                              },
                            ],
                          })(
                            <Input
                              fullWidth
                              placeholder={t('account-website-label')}
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('no_website', {
                            valuePropName: 'checked',
                          })(
                            <Checkbox
                              size="small"
                              label={t('no-website-label')}
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_first_name', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your first name',
                              },
                            ],
                          })(<Input fullWidth placeholder="First Name" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_last_name', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter last name',
                              },
                            ],
                          })(<Input fullWidth placeholder="Last Name" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_email', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter email',
                                type: 'email',
                              },
                            ],
                          })(<Input fullWidth placeholder="Email Address" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_email_confirm', {
                            rules: [
                              {
                                required: true,
                                message: 'Confirm your email address',
                                type: 'email',
                              },
                              {
                                validator: matchEmail,
                              },
                            ],
                          })(
                            <Input
                              fullWidth
                              placeholder="Confirm your email address"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_mobile', {
                            normalize: normalizePhone,
                            rules: [
                              {
                                required: true,
                                message: t(
                                  'phone-validation',
                                  'Phone Number is required!',
                                ),
                              },
                              { validator: validateMobilePhone },
                            ],
                          })(
                            <Input
                              fullWidth
                              type="tel"
                              placeholder="Mobile Phone"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_phone', {
                            normalize: normalizePhone,
                            rules: [{ required: false }],
                          })(
                            <Input
                              fullWidth
                              type="tel"
                              placeholder={t('account-phone-label')}
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormItem>
                          {getFieldDecorator('account_address1', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter address line 1',
                              },
                            ],
                          })(<Input fullWidth placeholder="Address line 1" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormItem>
                          {getFieldDecorator('account_address2', {
                            rules: [
                              {
                                required: false,
                                message: 'Please enter address line 2',
                              },
                            ],
                          })(<Input fullWidth placeholder="Address line 2" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormItem>
                          {getFieldDecorator('account_city', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter city',
                              },
                            ],
                          })(<Input fullWidth placeholder="City" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormItem>
                          {getFieldDecorator('account_state', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter state/provinces',
                              },
                            ],
                          })(
                            <Select
                              transformValue
                              fullWidth
                              options={STATES[country]}
                              placeholder="State/Provinces"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormItem>
                          {getFieldDecorator('account_zip', {
                            rules: [
                              {
                                required: true,
                                message: 'Please Zip/Postal code',
                              },
                            ],
                          })(<Input fullWidth placeholder="Zip/Postal code" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormItem>
                          {getFieldDecorator('account_country', {
                            initialValue: 'US',
                            rules: [
                              {
                                required: true,
                                message: 'Please select country',
                              },
                            ],
                          })(
                            <Select
                              transformValue
                              fullWidth
                              options={COUNTRIES}
                              placeholder="Country"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl
                          variant="standard"
                          margin="normal"
                          sx={{
                            border: '1px solid',
                            borderColor: 'grey.300',
                            padding: '10px 18px',
                            borderRadius: 50,
                          }}
                          fullWidth
                        >
                          <CardElement
                            options={{
                              style: {
                                base: {
                                  'fontFamily': 'Poppins,sans-serif',
                                  'fontSize': '14px',
                                  '::placeholder': {
                                    color: '#bbb',
                                  },
                                },
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormItem>
                          {getFieldDecorator('account_tax_id')(
                            <Input
                              fullWidth
                              placeholder="Exempt Tax ID - (Optional)"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                    </Grid>

                    <Box mb={2}>
                      <FormItem>
                        {getFieldDecorator('agreement', {
                          valuePropName: 'checked',
                          initialValue: false,
                          rules: [
                            {
                              validator: isChecked,
                              message: 'Please accept membership agreement',
                            },
                          ],
                        })(
                          <Checkbox
                            label={
                              <>
                                I agree to the{' '}
                                <a href={t('agreement_url')} target="_blank">
                                  Membership Agreement
                                </a>
                              </>
                            }
                          />,
                        )}
                      </FormItem>
                    </Box>
                    <Divider />
                    <Typography textAlign="center" sx={{ my: 2 }}>
                      You will be billed $0 today and then ${item.price} per{' '}
                      {item.freq} after your {item.trialDuration} Day Trial.
                    </Typography>
                    <Button
                      fullWidth
                      size="large"
                      onClick={onSubmit}
                      sx={{ py: 1.4 }}
                    >
                      Start My Free Trial
                    </Button>
                  </Form>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Spin>
      </Box>
    </>
  );
};

export default Form.create()(withTranslation('checkout')(Checkout));
